import { ApolloClient, ApolloProvider, from, HttpLink } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import LoginButton from "./LoginButton";
import LeftMenu from "./components/LeftMenu";
import Stocks from "./components/Stocks";
import Transactions from "./components/Transactions";
import Accounts from "./components/Accounts";
import AccountsConnect from "./components/AccountsConnect";
import Categories from "./components/Categories";
import Bilan from "./components/Bilan";
import Stats from "./components/Stats";
import Admin from "./components/Admin";
import AutomaticOperations from "./components/AutomaticOperations";
import { transformIdsLink } from "./utils/apolloLink";
import { createApolloCache } from "./utils/apolloCache";

// Composant qui affiche les détails de l'utilisateur connecté
const Profile: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();

  // Affichage pendant le chargement des données
  if (isLoading) {
    return <div className="text-center text-gray-500">Chargement...</div>;
  }

  // Affichage du profil utilisateur si authentifié
  return isAuthenticated && user ? (
    <div className="flex flex-col items-center space-y-4">
      <img
        className="rounded-full w-24 h-24"
        src={user.picture}
        alt={user.name}
      />
      <h2 className="text-xl font-bold">{user.name}</h2>
      <p className="text-gray-600">{user.email}</p>
    </div>
  ) : (
    <div className="text-center text-red-500">Non connecté</div>
  );
};

// Composant principal
function App() {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const [token, setToken] = React.useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setToken(null);
      return;
    }

    getAccessTokenSilently({
      authorizationParams: {
        scope: "read:current_user openid profile email offline_access",
        // Audience pour obtenir un access token JWT au lieu d'un ID token JWE
        audience: "https://julientassin.eu.auth0.com/api/v2/",
      },
    })
      .then((accessToken) => {
        setToken(accessToken);
      })
      .catch((error: any) => {
        console.error("Erreur lors de la récupération du token:", error);
        // Toute erreur OAuth (login_required, consent_required, missing_refresh_token,
        // invalid_grant…) indique que la session est expirée côté Auth0 — rediriger vers login.
        if (error.error) {
          loginWithRedirect({
            authorizationParams: {
              scope: "read:current_user openid profile email offline_access",
              prompt: 'login',
            },
          });
        }
      });
  }, [getAccessTokenSilently, isAuthenticated, loginWithRedirect]);

  const httpLink = new HttpLink({
    uri: "https://api-gateway-2.vercel.app/graphql",
    headers: { authorization: `Bearer ${token}` },
  });

  const client = new ApolloClient({
    link: from([transformIdsLink, httpLink]),
    cache: createApolloCache(),
  });

  return (
    <ApolloProvider client={client}>
      <BrowserRouter>
        <div className="flex min-h-screen bg-gray-100">
          {/* Menu de navigation latéral */}
          <LeftMenu />

          {/* Contenu principal avec marge à gauche pour accommoder le menu fixe */}
          <div className="flex-1 ml-64 p-8">
            <header className="mb-8">
              <div className="flex justify-end">
                <LoginButton />
              </div>
            </header>

            <main>
              <Routes>
                <Route path="/profile" element={<Profile />} />
                <Route path="/stocks" element={<Stocks />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/accounts/connect" element={<AccountsConnect />} />
                <Route path="/bilan" element={<Bilan />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/automatic-operations" element={<AutomaticOperations />} />
                <Route
                  path="/"
                  element={
                    <div className="space-y-4">
                      <h1 className="text-2xl font-bold">
                        Bienvenue sur l'application Money
                      </h1>
                      <p className="text-lg">
                        Utilisez le menu de gauche pour naviguer dans
                        l'application.
                      </p>
                    </div>
                  }
                />
              </Routes>
            </main>
          </div>
        </div>
      </BrowserRouter>
    </ApolloProvider>
  );
}

export default App;
